import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";

import { ApiError, api, bytes } from "../api";

interface Plan {
  id: string;
  code: string;
  name: string;
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
}

interface Template {
  id: string;
  name: string;
  os_family: string;
  os_version: string;
}

interface Ordered {
  service_id: string;
  external_id: string;
  hostname: string;
  ip_address: string;
  node: string;
  root_password: string;
}

export function Order() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [planId, setPlanId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [hostname, setHostname] = useState("");
  const [sshKey, setSshKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [built, setBuilt] = useState<Ordered | null>(null);

  const catalog = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.get<{ plans: Plan[]; templates: Template[] }>("/api/catalog"),
  });

  const place = useMutation({
    mutationFn: () =>
      api.post<Ordered>("/api/services/order", {
        plan_id: planId,
        template_id: templateId,
        hostname: hostname.trim().toLowerCase(),
        ssh_public_key: sshKey.trim() || null,
      }),
    onSuccess: (result) => {
      setBuilt(result);
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "The server could not be built"),
  });

  // The credentials screen. Shown once and never again, so it says so plainly
  // and refuses to be a step you can click past without noticing.
  if (built) {
    return (
      <>
        <div className="page-head">
          <div>
            <p className="label">Ready</p>
            <h1>{built.hostname}</h1>
          </div>
        </div>

        <section className="panel">
          <dl className="specs">
            <div>
              <dt className="label">Address</dt>
              <dd>{built.ip_address}</dd>
            </div>
            <div>
              <dt className="label">Host</dt>
              <dd>{built.node}</dd>
            </div>
            <div>
              <dt className="label">ID</dt>
              <dd>{built.external_id}</dd>
            </div>
          </dl>
        </section>

        <section className="panel handover">
          <p className="label">Root password</p>
          <p className="handover__secret mono">{built.root_password}</p>
          <p className="handover__warning">
            Save this now. It is not stored anywhere and cannot be shown again — if you lose it,
            reset it from the console.
          </p>
          <div className="btn-group" style={{ marginTop: "1.25rem" }}>
            <button
              type="button"
              className="btn"
              onClick={() => navigator.clipboard?.writeText(built.root_password)}
            >
              Copy password
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => navigate(`/services/${built.service_id}`)}
            >
              I have saved it — open the server
            </button>
          </div>
        </section>

        <p className="muted" style={{ marginTop: "1rem", fontSize: "var(--step--1)" }}>
          Connect with <span className="mono">ssh root@{built.ip_address}</span> once it has
          started.
        </p>
      </>
    );
  }

  const plans = catalog.data?.plans ?? [];
  const templates = catalog.data?.templates ?? [];
  const ready = planId && templateId && hostname.trim().length > 0;

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/services" className="label" style={{ textDecoration: "none" }}>
            ← All services
          </Link>
          <h1>Order a server</h1>
          <p className="lede">
            A Linux container with a public address, ready in about a minute.
          </p>
        </div>
      </div>

      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}

      {catalog.isPending && <p className="muted">Loading…</p>}

      {!catalog.isPending && (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            place.mutate();
          }}
        >
          <section>
            <h2>Size</h2>
            <div className="ledger">
              {plans.map((plan) => (
                <label
                  key={plan.id}
                  className={`pick${planId === plan.id ? " pick--on" : ""}`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={plan.id}
                    checked={planId === plan.id}
                    onChange={() => setPlanId(plan.id)}
                  />
                  <span className="pick__name">{plan.name}</span>
                  <span className="pick__spec mono">
                    {plan.cpu_cores} vCPU · {bytes(plan.memory_mb * 1024 * 1024)} ·{" "}
                    {plan.disk_gb} GB
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h2>Operating system</h2>
            <div className="ledger">
              {templates.map((template) => (
                <label
                  key={template.id}
                  className={`pick${templateId === template.id ? " pick--on" : ""}`}
                >
                  <input
                    type="radio"
                    name="template"
                    value={template.id}
                    checked={templateId === template.id}
                    onChange={() => setTemplateId(template.id)}
                  />
                  <span className="pick__name">{template.name}</span>
                  <span className="pick__spec mono">{template.os_family}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="panel">
            <label className="field">
              <span className="label">Hostname</span>
              <input
                type="text"
                required
                placeholder="mail.example.co.za"
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
              />
              <span className="muted" style={{ fontSize: "var(--step--1)" }}>
                Letters, digits, hyphens and dots.
              </span>
            </label>

            <label className="field" style={{ marginBottom: 0 }}>
              <span className="label">SSH public key — optional</span>
              <textarea
                rows={3}
                placeholder="ssh-ed25519 AAAA…"
                value={sshKey}
                onChange={(e) => setSshKey(e.target.value)}
              />
              <span className="muted" style={{ fontSize: "var(--step--1)" }}>
                Added to root's authorised keys. Without one, use the password we show you next.
              </span>
            </label>
          </section>

          <div className="spread">
            <p className="muted" style={{ margin: 0, fontSize: "var(--step--1)" }}>
              An address is reserved for this server when you order.
            </p>
            <button type="submit" className="btn btn--primary" disabled={!ready || place.isPending}>
              {place.isPending ? "Building…" : "Create server"}
            </button>
          </div>
        </form>
      )}
    </>
  );
}
